import { create } from "zustand";
import type {
  PosCajeroConPin,
  PosEstado,
  PosEstadoDelMandato,
  PosVerificacionDePin,
} from "@/platform/types";
import { motivoDelBloqueo } from "./estadoDeLaCaja";
import { esSesionDeCaja } from "./sesionDeCaja";

/**
 * Quién vende en la caja (#346 AC-2 y AC-3; §2.6 de OFFLINE-POS-RONDA-2.md).
 * Sin red no hay una sesión que lo diga: el cajero entra con su PIN, y sus
 * ventas suben a su nombre (`soldBy`). La sesión, cuando vuelve, sólo sube.
 */

export interface CajeroDeLaCaja {
  userId: string;
  nombre: string;
  /** El negocio de la caja en la que entró: en otra no vale. */
  businessId: string;
}

export const useCajeroDeLaCaja = create<{ cajero: CajeroDeLaCaja | null }>(
  () => ({ cajero: null })
);

/** El cajero dio bien su PIN en la caja de ese negocio. */
export const entrarElCajero = (businessId: string, cajero: PosCajeroConPin) =>
  useCajeroDeLaCaja.setState({
    cajero: { userId: cajero.userId, nombre: cajero.nombre, businessId },
  });

/** Bloquea la caja: sale el cajero, sin borrar nada (§2.6). */
export const bloquearLaCaja = () =>
  useCajeroDeLaCaja.setState({ cajero: null });

/**
 * La caja pide el PIN cuando no hay una sesión que pueda vender: sin red, o
 * con el latido en `401` o en `403 not_permitted` (§2.5 y §2.6).
 */
export function pideElPin(estado: Pick<PosEstado, "conexion" | "bloqueo">) {
  return (
    estado.conexion === "sin-conexion" ||
    estado.bloqueo?.tipo === "sesion" ||
    estado.bloqueo?.tipo === "sin-permiso"
  );
}

/**
 * Si el cajero puede cargar su PIN ahora (§2.6): con red y con su propia
 * sesión, en una caja de un core que da mandatos (hay uno, o le falta la
 * primera hoja). Lo usan el mostrador y el panel de la caja.
 */
export function sePuedeCargarElPin(
  estado: Pick<
    PosEstado,
    "conexion" | "bloqueo" | "mandatoId" | "razonSinMandato"
  >,
  usuario: unknown
): boolean {
  const conMandatos =
    estado.mandatoId !== null || estado.razonSinMandato === "no_sheet_yet";
  return conMandatos && !esSesionDeCaja(usuario) && !pideElPin(estado);
}

/** §2.7: el mandato pasó su `hardUntil` (D17). */
export const PASARON_72_HORAS =
  "Pasaron más de 3 días sin conexión: conectate para seguir vendiendo.";

/** Lo que dice la pantalla después de un intento (§2.7). */
export function mensajeDelPin(
  verificacion: PosVerificacionDePin,
  ahora = new Date()
): string | null {
  switch (verificacion.tipo) {
    case "correcto":
      return null;
    case "incorrecto":
      return verificacion.quedan === 1
        ? "PIN incorrecto: queda 1 intento."
        : `PIN incorrecto: quedan ${verificacion.quedan} intentos.`;
    case "bloqueado": {
      const segundos = Math.max(
        1,
        Math.ceil(
          (new Date(verificacion.hasta).getTime() - ahora.getTime()) / 1000
        )
      );
      const espera =
        segundos >= 60 ? `${Math.ceil(segundos / 60)} min` : `${segundos} s`;
      return `Esperá un momento y probá de nuevo (${espera}).`;
    }
    case "sin-pin":
      return "Cargá tu PIN con conexión.";
    case "sin-mandato":
      return verificacion.vencido
        ? PASARON_72_HORAS
        : "Esta caja no puede vender sin conexión.";
    case "sin-permiso":
      return motivoDelBloqueo({ tipo: "sin-permiso" });
  }
}

/**
 * El usuario al que core le dijo `403 not_permitted` (§2.4 y §2.7): el de la
 * sesión. Con él no se vende, tampoco con su PIN; los demás cajeros siguen
 * hasta que venza el mandato. `null` si no hay ninguno.
 */
export function usuarioSinPermiso(
  estado: Pick<PosEstado, "bloqueo">,
  usuarioId: string | null | undefined
): string | null {
  return estado.bloqueo?.tipo === "sin-permiso" && usuarioId ? usuarioId : null;
}

/**
 * Quiénes entran con PIN (§2.6): los cajeros del mandato vigente que tienen
 * PIN en este equipo, menos el usuario sin permiso (`sinPermiso`). Es la misma
 * regla que aplica Rust al verificar.
 */
export function cajerosQueEntran(
  mandato: PosEstadoDelMandato | null | undefined,
  sinPermiso: string | null = null
): PosCajeroConPin[] {
  const vigente = mandato?.vigente?.mandato;
  if (!vigente) return [];
  const delMandato = new Set(vigente.cashiers.map((c) => c.userId));
  return mandato.cajerosConPin.filter(
    (c) => delMandato.has(c.userId) && c.userId !== sinPermiso
  );
}

export type PuertaDeLaCaja =
  | { tipo: "mostrador" }
  /** Bajo el mandato, todavía no se sabe si hay que pedir el PIN. */
  | { tipo: "cargando" }
  | { tipo: "pide-pin"; cajeros: PosCajeroConPin[] };

/**
 * Si el mostrador pide el PIN antes de vender (D23 y D24): en una caja de este
 * local, cuando la venta va bajo el mandato, porque no hay red o no hay una
 * sesión que pueda subirla, y nadie entró con su PIN en esta caja. Hace falta
 * un mandato vigente, sin vencer, con cajeros que tengan PIN: si no, no hay a
 * quién pedírselo y el mostrador sigue como en la ronda 1.
 */
export function puertaDeLaCaja({
  esCaja,
  cargando,
  businessId,
  sinSesion,
  estado,
  mandato,
  cajero,
  usuarioId,
}: {
  esCaja: boolean;
  /** La terminal o el mandato todavía no se leyeron del almacén. */
  cargando: boolean;
  businessId: string | null | undefined;
  /** La caja sin sesión: siempre va bajo el mandato. */
  sinSesion: boolean;
  estado: Pick<PosEstado, "conexion" | "bloqueo">;
  mandato: PosEstadoDelMandato | null | undefined;
  cajero: CajeroDeLaCaja | null;
  /** El usuario de la sesión: con `403 not_permitted`, ése no entra (§2.7). */
  usuarioId?: string | null;
}): PuertaDeLaCaja {
  const bajoMandato = sinSesion || pideElPin(estado);
  const sinPermiso = usuarioSinPermiso(estado, usuarioId);
  // Quien ya había entrado y es el usuario sin permiso, ya no vende.
  const entro =
    !!cajero &&
    cajero.businessId === businessId &&
    cajero.userId !== sinPermiso;
  if (!bajoMandato || entro) return { tipo: "mostrador" };
  // Que no se vea el mostrador antes de pedir el PIN.
  if (cargando) return { tipo: "cargando" };
  if (!esCaja || !mandato?.vigente) {
    return { tipo: "mostrador" };
  }
  if (mandato.vencido) return { tipo: "mostrador" };
  const cajeros = cajerosQueEntran(mandato, sinPermiso);
  return cajeros.length > 0
    ? { tipo: "pide-pin", cajeros }
    : { tipo: "mostrador" };
}
